import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  ButtonGroup,
  Badge,
  Icon,
  Divider,
  Grid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Center,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { SelectControl, TextareaControl } from 'formik-chakra-ui';
import {
  FiCheckCircle,
  FiCalendar,
  FiPhone,
  FiPlus,
  FiEdit3,
  FiBriefcase,
} from 'react-icons/fi';
import moment from 'moment';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';
import {
  supabase,
  isSupabaseConfigured,
  transformTaskToCamelCase,
  transformTaskToSnakeCase,
} from '../../../utils/supabase';
import { buildDncUpdate } from '../../../utils/dnc';
import { getNurtureRouteForSource } from '../../../constants/crmConstants';
import { useAuth } from '../../../contexts/AuthContext';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { DatePickerField } from '../../../components/DatePicker';
import { updateDealNextActionDue } from '../../../hooks/useTasks';
import UnifiedActivityTimeline from '../../../components/CRM/UnifiedActivityTimeline';
import LogActivityModal from '../../../components/CRM/LogActivityModal';
import LostDealReasonModal from '../../../components/CRM/LostDealReasonModal';
import MoveToNurtureModal from '../../../components/CRM/Campaigns/MoveToNurtureModal';
import UnifiedEventModal from '../../../components/CRM/Calendar/UnifiedEventModal';
import TaskDetailModal from '../../../components/Tasks/TaskDetailModal';
import TaskList from '../../../components/Tasks/TaskList';
import CallHistoryPanel from '../../../components/CRM/Dialpad/CallHistoryPanel';

/**
 * WorkPanel — the center column of the Person Workspace.
 *
 * Tab "Work": the Log Activity form ported from DealPage with IDENTICAL save
 * semantics (crm_deals update → DNC on do-not-contact → crm_activities insert
 * → handleAutomationTriggers callable → Lost/Nurture/task-prompt follow-ups),
 * plus a unified activity timeline across the contact and ALL their deals.
 * With no deal selected, the tab swaps to contact-level logging via the
 * existing LogActivityModal — a person with zero deals is still fully workable.
 *
 * Tab "Tasks": every task for this person in one list — contact-level tasks
 * and tasks on any of their deals.
 *
 * Tab "Dialpad": call/SMS history (same gate as DealPage).
 */

// Same schema as DealPage's Log Activity form
const validationSchema = Yup.object().shape({
  stage: Yup.string().required('Status is required'),
  disposition: Yup.string(),
  nextAction: Yup.string(),
  nextActionDue: Yup.date().nullable(),
  notes: Yup.string().when('disposition', {
    is: (val) => val && val !== '',
    then: (schema) => schema.required('Notes are required when logging a disposition'),
    otherwise: (schema) => schema,
  }),
});

/* ------------------------------------------------------------------ */
/* Unified tasks: contact-level + every deal of this person            */
/* ------------------------------------------------------------------ */

function useWorkspaceTasks(contact, dealIds) {
  const [tasks, setTasks] = useState([]);
  const contactId = contact?.id;
  const dealIdsKey = dealIds.join(',');
  const instanceId = useRef(Math.random().toString(36).slice(2, 8));

  const fetchTasks = useCallback(async () => {
    if (!contactId || !isSupabaseConfigured()) {
      setTasks([]);
      return;
    }
    const orParts = [`contact_id.eq.${contactId}`];
    if (dealIdsKey) orParts.push(`deal_id.in.(${dealIdsKey})`);

    const { data, error } = await supabase
      .from('crm_tasks')
      .select('*, crm_contacts!contact_id(id, first_name, last_name, email), crm_deals!deal_id(id, deal_name, stage)')
      .or(orParts.join(','))
      .order('due_date', { ascending: true });

    if (error) {
      console.error('[useWorkspaceTasks] Error fetching tasks:', error);
      setTasks([]);
    } else {
      setTasks((data || []).map(transformTaskToCamelCase));
    }
  }, [contactId, dealIdsKey]);

  useEffect(() => {
    fetchTasks();
    if (!contactId || !isSupabaseConfigured()) return undefined;

    // postgres_changes filters can't express OR(contact, deals), so listen to
    // the table and refetch debounced — same pattern as DealPage's work queue.
    let timer = null;
    const scheduleRefetch = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fetchTasks, 300);
    };
    const channel = supabase
      .channel(`ws-tasks-${contactId}-${instanceId.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_tasks' }, scheduleRefetch)
      .subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [contactId, fetchTasks]);

  // Ports of useDealTasks' mutations, resolving the deal per task so
  // next_action_due stays in sync on whichever deal the task belongs to.
  const createTask = async (taskData) => {
    if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
    try {
      const createdBy = taskData.createdBy || taskData.assignedBy || taskData.assignedTo;
      const assignedTo = taskData.assignedTo || taskData.createdBy || taskData.assignedBy;
      if (!createdBy) {
        return { success: false, error: 'User ID is required to create a task. Please ensure you are logged in.' };
      }

      const dealId = taskData.dealId || (taskData.relatedTo?.type === 'deal' ? taskData.relatedTo.id : null);
      const relatedTo = taskData.relatedTo || (dealId
        ? { type: 'deal', id: dealId, name: taskData.relatedEntityName || 'Deal' }
        : { type: 'contact', id: contactId, name: `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Contact' });

      const newTask = transformTaskToSnakeCase({
        ...taskData,
        createdBy,
        assignedTo: assignedTo || createdBy,
        status: taskData.status || 'pending',
        priority: taskData.priority || 'medium',
        relatedTo,
        ...(dealId ? { dealId } : {}),
        contactId: taskData.contactId !== undefined ? taskData.contactId : contactId,
      });

      const { data, error } = await supabase.from('crm_tasks').insert(newTask).select('*').single();
      if (error) return { success: false, error: error.message };

      const created = transformTaskToCamelCase(data);
      setTasks((prev) => [...prev, created].sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      }));

      if (dealId) await updateDealNextActionDue(dealId);
      return { success: true, id: data.id };
    } catch (error) {
      console.error('[useWorkspaceTasks] Error creating task:', error);
      return { success: false, error: error.message };
    }
  };

  const dealIdForTask = (taskId) => tasks.find((t) => t.id === taskId)?.dealId || null;

  const completeTask = async (taskId) => {
    if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
    try {
      const completedAt = new Date().toISOString();
      const { error } = await supabase
        .from('crm_tasks')
        .update({ status: 'completed', completed_at: completedAt })
        .eq('id', taskId);
      if (error) return { success: false, error: error.message };
      const dealId = dealIdForTask(taskId);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'completed', completedAt: new Date(completedAt) } : t)));
      if (dealId) await updateDealNextActionDue(dealId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const reopenTask = async (taskId) => {
    if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase
        .from('crm_tasks')
        .update({ status: 'pending', completed_at: null })
        .eq('id', taskId);
      if (error) return { success: false, error: error.message };
      const dealId = dealIdForTask(taskId);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'pending', completedAt: null } : t)));
      if (dealId) await updateDealNextActionDue(dealId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteTask = async (taskId) => {
    if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
    try {
      const dealId = dealIdForTask(taskId);
      const { error } = await supabase.from('crm_tasks').delete().eq('id', taskId);
      if (error) return { success: false, error: error.message };
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (dealId) await updateDealNextActionDue(dealId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return { tasks, createTask, completeTask, reopenTask, deleteTask };
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function WorkPanel({
  contact,
  deal,
  deals,
  dealIds,
  crmUsers,
  dialpadEnabled,
  onDealPatched,
  onCreateDeal,
}) {
  const { currentUser, Roles } = useAuth();
  const { dispositions, nextActions, getStagesForPicker } = useCrmSettings();
  const toast = useToast();
  const isDTCLoanOfficer = Roles?.includes('DTC Loan Officer');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [timelineScope, setTimelineScope] = useState('all'); // 'all' | 'deal'
  const [showTaskPrompt, setShowTaskPrompt] = useState(false);
  const [taskPromptData, setTaskPromptData] = useState(null);
  const [nurtureRouteData, setNurtureRouteData] = useState(null);
  const [pendingLostStageUpdate, setPendingLostStageUpdate] = useState(null);
  const [isLostReasonSubmitting, setIsLostReasonSubmitting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const { isOpen: isLostReasonOpen, onOpen: onLostReasonOpen, onClose: onLostReasonClose } = useDisclosure();
  const { isOpen: isNurtureOpen, onOpen: onNurtureOpen, onClose: onNurtureClose } = useDisclosure();
  const { isOpen: isUnifiedEventOpen, onOpen: onUnifiedEventOpen, onClose: onUnifiedEventClose } = useDisclosure();
  const { isOpen: isTaskDetailOpen, onOpen: onTaskDetailOpen, onClose: onTaskDetailClose } = useDisclosure();
  const { isOpen: isLogActivityOpen, onOpen: onLogActivityOpen, onClose: onLogActivityClose } = useDisclosure();

  const { tasks, createTask, completeTask, reopenTask, deleteTask } = useWorkspaceTasks(contact, dealIds);
  const activeTasks = tasks.filter((t) => t.status !== 'completed');

  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const noDealBG = useColorModeValue('purple.50', 'purple.900');

  const charLimit = 32000;

  /* ---------------- Log Activity: identical semantics to DealPage ---------------- */

  const performStageUpdate = async (values, opts = {}) => {
    const changes = [];
    if (values.stage !== deal.stage) changes.push(`Stage changed: ${deal.stage} → ${values.stage}`);
    if (values.nextAction !== deal.nextAction) changes.push(`Next action: ${values.nextAction}`);
    if (values.nextActionDue) changes.push(`Due date: ${moment(values.nextActionDue).format('MMM DD, YYYY')}`);
    if (opts.lostReasonLabel) changes.push(`Lost Reason: ${opts.lostReasonLabel}`);

    // 1) Deal update (same columns as DealPage)
    const { error: dealError } = await supabase
      .from('crm_deals')
      .update({
        stage: values.stage,
        next_action: values.nextAction || '',
        next_action_due: values.nextActionDue ? new Date(values.nextActionDue).toISOString() : null,
        is_at_risk: false,
      })
      .eq('id', deal.id);
    if (dealError) throw dealError;

    // 2) DNC (Everything) on do-not-contact disposition
    if (values.disposition === 'do-not-contact' && contact?.id) {
      await supabase
        .from('crm_contacts')
        .update(buildDncUpdate({}, { everything: true }))
        .eq('id', contact.id);
      changes.push('⚠️ Marked as Do Not Contact');
    }

    // 3) Activity insert
    let activityContent = values.notes || 'Deal updated';
    if (opts.lostReasonDetails && values.stage === 'deal-lost') {
      activityContent = `🚫 Deal marked as Lost\n\nReason: ${opts.lostReasonLabel}\n\nDetails: "${opts.lostReasonDetails}"${values.notes ? `\n\nNotes: ${values.notes}` : ''}`;
    }

    const { error: activityError } = await supabase.from('crm_activities').insert({
      deal_id: deal.id,
      contact_id: contact?.id || null,
      author_id: currentUser.uid,
      // Parity with DealPage: normal submits log 'update'/'disposition';
      // the Lost-reason flow logs 'stage_change' (its performStageUpdate path).
      type: values.disposition ? 'disposition' : (opts.lostReason ? 'stage_change' : 'update'),
      content: activityContent,
      metadata: {
        authorName: currentUser.displayName || currentUser.email,
        authorPhotoURL: currentUser.photoURL || '',
        disposition: values.disposition || '',
        changes,
        previousStage: deal.stage,
        newStage: values.stage,
        source: 'person_workspace',
        ...(opts.lostReason && {
          lostReason: opts.lostReason,
          lostReasonLabel: opts.lostReasonLabel,
          lostReasonDetails: opts.lostReasonDetails,
        }),
      },
    });
    if (activityError) throw activityError;

    // 4) Automation triggers (fire-and-forget)
    try {
      const fireTrigger = httpsCallable(functionsUsEast1, 'handleAutomationTriggers');
      if (values.stage !== deal.stage) {
        fireTrigger({ eventType: 'deal.stage_changed', payload: { contactId: contact.id, dealId: deal.id, fromStage: deal.stage, toStage: values.stage } });
      }
      if (values.disposition) {
        fireTrigger({ eventType: 'activity.disposition_logged', payload: { contactId: contact.id, dealId: deal.id, disposition: values.disposition } });
      }
    } catch (_) {}

    // 5) Instant UI updates
    onDealPatched(deal.id, {
      stage: values.stage,
      nextAction: values.nextAction || '',
      nextActionDue: values.nextActionDue || null,
      isAtRisk: false,
    });
    setActivityRefreshKey((k) => k + 1);

    // 6) Follow-ups: nurture routing wins over the generic task prompt
    const nurtureRoute = values.nextAction === 'nurture' ? getNurtureRouteForSource(deal.source) : null;
    if (nurtureRoute) {
      setNurtureRouteData(nurtureRoute);
      onNurtureOpen();
    } else if (values.nextAction && values.nextActionDue) {
      setTaskPromptData({
        nextAction: values.nextAction,
        nextActionDue: values.nextActionDue,
        dealName: deal.dealName,
        dealId: deal.id,
      });
      setShowTaskPrompt(true);
    }

    if (opts.resetForm) {
      opts.resetForm({
        values: {
          stage: values.stage,
          disposition: '',
          nextAction: values.nextAction,
          nextActionDue: values.nextActionDue,
          notes: '',
        },
      });
    }
  };

  const handleSubmit = async (values, actions) => {
    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    // DTC Loan Officer Lost Deal workflow — intercept, require a reason first
    if (values.stage === 'deal-lost' && deal.stage !== 'deal-lost' && isDTCLoanOfficer) {
      setPendingLostStageUpdate(values);
      onLostReasonOpen();
      return;
    }

    setIsSubmitting(true);
    try {
      await performStageUpdate(values, { resetForm: actions.resetForm });
      toast({ title: 'Activity logged', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: 'Error updating deal', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLostReasonConfirm = async ({ reason, reasonLabel, details }) => {
    if (!pendingLostStageUpdate) return;

    setIsLostReasonSubmitting(true);
    try {
      const sendLostDealNotification = httpsCallable(functionsUsEast1, 'sendLostDealNotification');
      await sendLostDealNotification({
        dealId: deal.id,
        reason,
        reasonDetails: details,
        loanOfficerName: currentUser.displayName || currentUser.email,
        loanOfficerEmail: currentUser.email,
      });

      await performStageUpdate(pendingLostStageUpdate, {
        lostReason: reason,
        lostReasonLabel: reasonLabel,
        lostReasonDetails: details,
      });

      toast({ title: 'Deal marked as Lost', description: 'Notification sent to management', status: 'success', duration: 3000 });
      onLostReasonClose();
      setPendingLostStageUpdate(null);
    } catch (error) {
      console.error('Error processing lost deal:', error);
      toast({ title: 'Error', description: error.message || 'Failed to process lost deal notification', status: 'error', duration: 5000 });
    } finally {
      setIsLostReasonSubmitting(false);
    }
  };

  const handleCreateTaskFromPrompt = async (option) => {
    if (option === 'auto') {
      if (!currentUser?.uid) {
        toast({ title: 'Failed to create task', description: 'You must be logged in to create tasks.', status: 'error', duration: 5000 });
        setShowTaskPrompt(false);
        setTaskPromptData(null);
        return;
      }

      const result = await createTask({
        title: `${nextActions.find((a) => a.value === taskPromptData.nextAction)?.label || taskPromptData.nextAction}: ${taskPromptData.dealName}`,
        description: `Next action for ${taskPromptData.dealName}`,
        type: 'crm',
        priority: 'medium',
        status: 'pending',
        assignedTo: currentUser.uid,
        createdBy: currentUser.uid,
        dueDate: taskPromptData.nextActionDue,
        dueTime: '',
        dealId: taskPromptData.dealId,
        relatedTo: { type: 'deal', id: taskPromptData.dealId, name: taskPromptData.dealName },
      });
      if (result.success) {
        toast({ title: 'Task created', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Failed to create task', description: result.error || 'Unknown error', status: 'error', duration: 5000 });
      }
    } else if (option === 'manual') {
      onUnifiedEventOpen();
    }
    setShowTaskPrompt(false);
    setTaskPromptData(null);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    onTaskDetailClose();
    onUnifiedEventOpen();
  };

  const initialValues = {
    stage: deal?.stage || '',
    disposition: '',
    nextAction: '',
    nextActionDue: null,
    notes: '',
  };

  const timelineDealIds = timelineScope === 'deal' && deal ? [deal.id] : dealIds;

  return (
    <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
      <Tabs colorScheme="blue">
        <TabList
          px={{ base: 2, md: 6 }}
          overflowX="auto"
          overflowY="hidden"
          sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        >
          <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }}>
            <HStack spacing={2}>
              <Icon as={FiCheckCircle} />
              <Text>Work</Text>
            </HStack>
          </Tab>
          <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }}>
            <HStack spacing={2}>
              <Icon as={FiCalendar} />
              <Text>Tasks</Text>
              <Badge ml={1} colorScheme="blue">{activeTasks.length}</Badge>
            </HStack>
          </Tab>
          {dialpadEnabled && (
            <Tab flexShrink={0} whiteSpace="nowrap" px={{ base: 3, md: 4 }}>
              <HStack spacing={2}>
                <Icon as={FiPhone} />
                <Text>Dialpad</Text>
              </HStack>
            </Tab>
          )}
        </TabList>

        <TabPanels>
          {/* ------------------- TAB 1: WORK ------------------- */}
          <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
            <VStack spacing={6} align="stretch">
              {deal ? (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ handleSubmit: submitForm, values, setFieldValue }) => (
                    <Box as="form" onSubmit={submitForm}>
                      <VStack spacing={5} align="stretch">
                        <HStack justify="space-between">
                          <Heading size={{ base: 'sm', md: 'md' }}>Log Activity</Heading>
                          <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                            {deal.dealName}
                          </Text>
                        </HStack>
                        <SelectControl name="stage" label="Stage / Status">
                          {getStagesForPicker(deal.stage).map((stage) => (
                            <option key={stage.value} value={stage.value}>{stage.label}</option>
                          ))}
                        </SelectControl>
                        <Grid templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }} gap={4}>
                          <SelectControl name="disposition" label="Disposition" minW={0}>
                            <option value="">Select Disposition</option>
                            {dispositions.map((disp) => (
                              <option key={disp.value} value={disp.value}>{disp.label}</option>
                            ))}
                          </SelectControl>
                          <SelectControl name="nextAction" label="Next Step" minW={0}>
                            {nextActions.map((action) => (
                              <option key={action.value} value={action.value}>{action.label}</option>
                            ))}
                          </SelectControl>
                          <Box
                            minW={0}
                            sx={{
                              '.react-datepicker-wrapper': { width: '100%' },
                              '.react-datepicker__input-container': { width: '100%' },
                              '.react-datepicker__input-container input': { width: '100%', minWidth: 0 },
                            }}
                          >
                            <DatePickerField
                              name="nextActionDue"
                              label="Due Date"
                              value={values.nextActionDue}
                              onChange={(date) => setFieldValue('nextActionDue', date)}
                            />
                          </Box>
                        </Grid>
                        <TextareaControl
                          name="notes"
                          label="Notes"
                          minW={0}
                          textareaProps={{
                            placeholder: 'Log call details, outcomes, or notes…',
                            rows: 4,
                            maxLength: charLimit,
                            minWidth: 0,
                          }}
                        />
                        <Button type="submit" colorScheme="blue" size="lg" isLoading={isSubmitting} rightIcon={<FiCheckCircle />}>
                          Save & Log Activity
                        </Button>
                      </VStack>
                    </Box>
                  )}
                </Formik>
              ) : (
                /* No deal — the person is still fully workable at contact level */
                <Box p={5} bg={noDealBG} borderRadius="lg">
                  <VStack spacing={3} align="stretch">
                    <HStack spacing={2}>
                      <Icon as={FiCheckCircle} color="purple.400" />
                      <Heading size="sm">No deals yet — work the contact</Heading>
                    </HStack>
                    <Text fontSize="sm" color={mutedColor}>
                      Log calls, texts, and outcomes directly on this person. Activities save to the
                      same timeline and fire the same automations — a deal can come later.
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      <Button size="sm" colorScheme="blue" leftIcon={<FiEdit3 />} onClick={onLogActivityOpen}>
                        Log Activity
                      </Button>
                      <Button size="sm" colorScheme="green" variant="outline" leftIcon={<FiBriefcase />} onClick={onCreateDeal}>
                        Create Deal
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              <Divider />

              {/* Unified timeline: contact + all deals in one stream */}
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Heading size="md">Activity Timeline</Heading>
                  {deal && dealIds.length > 1 && (
                    <ButtonGroup size="xs" isAttached variant="outline">
                      <Button
                        colorScheme={timelineScope === 'all' ? 'blue' : 'gray'}
                        variant={timelineScope === 'all' ? 'solid' : 'outline'}
                        onClick={() => setTimelineScope('all')}
                      >
                        All activity
                      </Button>
                      <Button
                        colorScheme={timelineScope === 'deal' ? 'blue' : 'gray'}
                        variant={timelineScope === 'deal' ? 'solid' : 'outline'}
                        onClick={() => setTimelineScope('deal')}
                      >
                        This deal
                      </Button>
                    </ButtonGroup>
                  )}
                </HStack>
                <UnifiedActivityTimeline
                  contactId={contact.id}
                  dealIds={timelineDealIds}
                  primaryDealId={timelineScope === 'deal' && deal ? deal.id : null}
                  maxHeight="520px"
                  refreshKey={activityRefreshKey}
                />
              </VStack>
            </VStack>
          </TabPanel>

          {/* ------------------- TAB 2: TASKS ------------------- */}
          <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <VStack align="flex-start" spacing={0}>
                  <Heading size={{ base: 'sm', md: 'md' }}>Tasks for this Person</Heading>
                  <Text fontSize="xs" color={mutedColor}>
                    Contact-level tasks plus tasks on every deal
                  </Text>
                </VStack>
                <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={onUnifiedEventOpen}>
                  New Task
                </Button>
              </HStack>
              {tasks.length === 0 ? (
                <Center py={12}>
                  <VStack>
                    <Icon as={FiCalendar} boxSize={12} color="gray.300" mb={3} />
                    <Text color="gray.500" fontSize="sm">No tasks for this person yet</Text>
                    <Text fontSize="xs" color="gray.400">Create a task to track follow-ups</Text>
                  </VStack>
                </Center>
              ) : (
                <TaskList
                  tasks={tasks}
                  onComplete={completeTask}
                  onReopen={reopenTask}
                  onEdit={handleEditTask}
                  onDelete={deleteTask}
                  onClick={(task) => {
                    setSelectedTask(task);
                    onTaskDetailOpen();
                  }}
                />
              )}
            </VStack>
          </TabPanel>

          {/* ------------------- TAB 3: DIALPAD ------------------- */}
          {dialpadEnabled && (
            <TabPanel px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
              <VStack align="stretch" spacing={4}>
                <Heading size={{ base: 'sm', md: 'md' }}>Dialpad History</Heading>
                <CallHistoryPanel contactId={contact.id} dealId={deal?.id} />
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* ---------------- Modals (same flows as DealPage/drawer) ---------------- */}

      {/* Follow-up task prompt after logging with a next action + due date */}
      <Modal isOpen={showTaskPrompt} onClose={() => setShowTaskPrompt(false)} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Create Task for Next Action?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontSize="sm" fontWeight="semibold">Would you like to create a task?</Text>
                <Text fontSize="xs">
                  Next Action: {taskPromptData && (nextActions.find((a) => a.value === taskPromptData.nextAction)?.label || taskPromptData.nextAction)}
                </Text>
                <Text fontSize="xs">Due: {taskPromptData && moment(taskPromptData.nextActionDue).format('MMM DD, YYYY')}</Text>
              </VStack>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => handleCreateTaskFromPrompt('no')}>No</Button>
            <Button variant="outline" mr={2} onClick={() => handleCreateTaskFromPrompt('manual')}>Create Manually</Button>
            <Button colorScheme="blue" onClick={() => handleCreateTaskFromPrompt('auto')}>Yes, Create Task</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Next Step = Nurture on a matching source → move into nurture campaign */}
      {deal && (
        <MoveToNurtureModal
          isOpen={isNurtureOpen}
          onClose={onNurtureClose}
          deal={deal}
          contact={contact}
          route={nurtureRouteData}
          onComplete={({ newDealTags }) => {
            if (Array.isArray(newDealTags)) {
              onDealPatched(deal.id, { tags: newDealTags });
            }
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* DTC Lost Deal reason (blocks the stage change until confirmed) */}
      {deal && (
        <LostDealReasonModal
          isOpen={isLostReasonOpen}
          onClose={() => {
            onLostReasonClose();
            setPendingLostStageUpdate(null);
          }}
          deal={deal}
          contact={contact}
          onConfirm={handleLostReasonConfirm}
          isLoading={isLostReasonSubmitting}
        />
      )}

      {/* Task / event creation + editing (saves internally, realtime refreshes) */}
      <UnifiedEventModal
        isOpen={isUnifiedEventOpen}
        onClose={() => {
          onUnifiedEventClose();
          setEditingTask(null);
        }}
        onSave={async () => {
          toast({ title: editingTask ? 'Task/Event updated' : 'Task/Event created', status: 'success', duration: 2000 });
          setEditingTask(null);
        }}
        initialEventType="task"
        prefilledEntity={deal ? { type: 'deal', data: deal, contact } : { type: 'contact', data: contact }}
        editingTask={editingTask}
        crmUsers={crmUsers}
      />

      <TaskDetailModal
        isOpen={isTaskDetailOpen}
        onClose={onTaskDetailClose}
        task={selectedTask}
        onEdit={handleEditTask}
        onDelete={async (taskId) => {
          const result = await deleteTask(taskId);
          if (result.success) {
            toast({ title: 'Task deleted', status: 'success', duration: 2000 });
            onTaskDetailClose();
          }
          return result;
        }}
        onComplete={completeTask}
        onReopen={reopenTask}
      />

      {/* Contact-level logging (no-deal mode) — existing modal, same triggers */}
      <LogActivityModal
        isOpen={isLogActivityOpen}
        onClose={onLogActivityClose}
        contact={contact}
        deals={deals}
        source="person_workspace"
        onCreateTask={(taskData) => createTask(taskData)}
        onLogged={() => setActivityRefreshKey((k) => k + 1)}
      />
    </Box>
  );
}
